import { type Database } from "@fitness-app/supabase";

import {
  type ScheduledAiAgentMessageAutomation,
  type ScheduledAiReportAutomation,
  type ScheduledAssignHabitsAutomation,
  type ScheduledEmailIntegrationAutomation,
  type ScheduledEnrollChallengeAutomation,
  type ScheduledFitnessAppAutomation,
  type ScheduledMessagesAutomation,
  type ScheduledPopUpMessagesAutomation,
  type ScheduledPopUpMessagesAutomationFromAutomation,
  type ScheduledSurveyAutomation,
  type ScheduledTeamMemberTaskAutomation,
} from "./AutomationTemplate";
import { type ChatType, type FileMessageContent } from "./Chat";
import { type ClientProfileGoal, type TimePerWeek, type TrainingLimitation } from "./ClientProfile";
import { type DietaryPreference } from "./MealsPlan";
import { type Attachment } from "./shared";
import { type ClientExperience, type PlaceToWorkout } from "./SurveyTemplate";
import {
  type ExceptionWeek,
  type ProgramSchedule,
  type ProgramWorkout,
  type TrainingProgram,
  type TrainingProgramDetails,
} from "./TrainingProgram";

export type ProgramAutomationEntity = Database["public"]["Tables"]["program_automation"]["Row"];
export type ProgramAutomationTaskEntity = Database["public"]["Tables"]["program_automation_task"]["Row"];
export type PlannedTasksSequenceEntity = Database["public"]["Tables"]["planned_tasks_sequence"]["Row"];

export interface PlannedTasksSequenceMetadata {
  messages?: string[];
  /** Sent with the last message of every delivery (TRE-2306). */
  attachments?: FileMessageContent[];
  filters?: { type: string; tags?: string[]; condition?: string }[];
  authorId?: string;
  authorName?: string;
  authorRole?: string;
  /** false = announcement channel; absent = legacy row created before TRE-801 (regular chat). */
  sendToDefaultChannel?: boolean;
  numberOfSequences?: number;
  dates?: {
    sendAt: number;
    sendDate: string;
    sentTime: {
      hours: number;
      minutes: number;
    };
  }[];
}

export interface PlannedTasksSequence extends Omit<PlannedTasksSequenceEntity, "metadata"> {
  metadata: PlannedTasksSequenceMetadata | null;
}

export type WeekdaysNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export type WeekdayNumberToString = `${WeekdaysNumber}`;

export enum ProgramAutomationScheduleAnchor {
  WeekDay = "week_day",
  PackageStart = "package_start",
  PackageEnd = "package_end",
  PackageProgress = "package_progress",
}

export type ProgramAutomationTaskScheduleTiming =
  | {
      anchor: ProgramAutomationScheduleAnchor.WeekDay;
    }
  | {
      anchor: ProgramAutomationScheduleAnchor.PackageStart;
      offsetDays: number;
    }
  | {
      anchor: ProgramAutomationScheduleAnchor.PackageEnd;
      offsetDays: number;
    }
  | {
      anchor: ProgramAutomationScheduleAnchor.PackageProgress;
      percent: number;
      offsetDays?: number;
    };

export interface ProgramAutomationPackageRange {
  startDate: string;
  endDate: string;
}

export type ProgramAutomationScheduledTask = (
  | ScheduledMessagesAutomation
  | ScheduledEmailIntegrationAutomation
  | ScheduledSurveyAutomation
  | ScheduledPopUpMessagesAutomationFromAutomation
  | ScheduledFitnessAppAutomation
  | ScheduledTeamMemberTaskAutomation
  | ScheduledAiAgentMessageAutomation
  | ScheduledAiReportAutomation
  | ScheduledAssignHabitsAutomation
  | ScheduledEnrollChallengeAutomation
) & {
  scheduleTiming?: ProgramAutomationTaskScheduleTiming | null;
};

export type DayContent = {
  id: string;
  items: ProgramAutomationScheduledTask[];
};

export type AutomationWeek = Partial<Record<WeekdayNumberToString, DayContent>>;
export type AutomationWeekWithWorkouts = Record<
  WeekdayNumberToString,
  DayContent & { workouts?: ProgramWorkout[] | null }
>;
export type WeekNumber = string;

/**
 * TRE-1605 — profile-based variant matching for scheduled workout routines.
 * Routines sharing the same startWeek form a variant group; the executor picks the
 * variant whose rules match the client's profile (ankieta 2.0). Every specified field
 * must match (unspecified = wildcard); excludedLimitations rejects the variant when the
 * client reports any of the listed limitations. A routine without matchRules is the
 * group's default/fallback, which keeps legacy automations 100% backward compatible.
 */
export interface RoutineMatchRules {
  placeToWorkout?: PlaceToWorkout[];
  experience?: ClientExperience[];
  timePerWeek?: TimePerWeek[];
  excludedLimitations?: TrainingLimitation[];
}

export interface WorkoutRoutine extends TrainingProgram {
  duration: number;
  id: string;
  programId?: string;
  schedule: {
    exceptionWeeks: Record<string, ExceptionWeek | null>;
    weekSchedule: ProgramSchedule;
  } | null;
  startWeek: number;
  endWeek: number;
  matchRules?: RoutineMatchRules | null;
  programWorkouts: TrainingProgramDetails;
  additionalComment: string;
  routineId: string;
  keepOldTrainingWithoutStatus?: boolean;
  requireFullFirstWeek?: boolean | null;
  creator?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export type ScheduledItems = {
  id: string;
  sendDate: string;
  items: ProgramAutomationScheduledTask[];
};

export enum ScheduledNutritionType {
  ByCurrentWeight = "byCurrentWeight",
  AssignedByTrainer = "assignedByTrainer",
  /** TRE-1603 — AI-generated diet window: the agent generates the plan instead of picking
   *  a preset from weight ranges, so there are NO `ranges`. Generation params come from the
   *  automation's `aiNutritionPlan` config; this entry only carries the window timing. */
  AiGenerated = "aiGenerated",
}

export type NutritionByType = {
  [DietaryPreference.NoPreference]: { id: string; name: string };
} & { [key in DietaryPreference]?: { id: string; name: string } };

/**
 * TRE-1605 — optional per-goal nutrition overrides for a weight range. When the client's
 * profile goal has an entry, its plans override the range's base `nutrition` per dietary
 * preference (missing preferences fall back to the base map). No entry = base behavior.
 */
export type NutritionByGoal = Partial<Record<ClientProfileGoal, Partial<NutritionByType>>>;

export interface MiddleRange {
  from: number;
  to: number;
  nutrition: NutritionByType;
  nutritionByGoal?: NutritionByGoal;
}

export interface StartRange {
  from: null;
  to: number;
  nutrition: NutritionByType;
  nutritionByGoal?: NutritionByGoal;
}

export interface EndRange {
  from: number;
  to: null;
  nutrition: NutritionByType;
  nutritionByGoal?: NutritionByGoal;
}

export interface ScheduledNutritionByCurrentWeight {
  startWeek: number;
  endWeek: number | null;
  additionalAttachments: Attachment[];
  additionalComment: string;
  id: string;
  type: ScheduledNutritionType.ByCurrentWeight;
  sendDate: {
    hours: number;
    minutes: number;
    week: number;
    day: number;
  } | null;
  ranges: [StartRange, ...MiddleRange[], EndRange];
}

export interface ScheduledNutritionAssignedByTrainer {
  startDate: string | null;
  endDate: string | null;
  id: string;
  type: ScheduledNutritionType.AssignedByTrainer;
  mealsPlanId: string;
  mealsPlanName: string;
}

/**
 * TRE-1603 — an AI-generated diet window. Mirrors ScheduledNutritionByCurrentWeight's timing
 * fields (so the existing scheduler's per-week sendDate math is reused unchanged) but has NO
 * `ranges` — the agent generates the plan. The static ↔ ai split is the `type` discriminator:
 * `ByCurrentWeight` = static (ranges), `AiGenerated` = ai (no ranges).
 */
export interface ScheduledNutritionAiGenerated {
  startWeek: number;
  endWeek: number | null;
  id: string;
  type: ScheduledNutritionType.AiGenerated;
  /**
   * TRE-1707 — AiNutritionPlanConfig.id this window regenerates from. Absent on windows
   * scheduled before TRE-1707, which fall back to the automation's first AI diet config.
   */
  configId?: string | null;
  sendDate: {
    hours: number;
    minutes: number;
    week: number;
    day: number;
  } | null;
}

export type ScheduledNutrition =
  | ScheduledNutritionByCurrentWeight
  | ScheduledNutritionAssignedByTrainer
  | ScheduledNutritionAiGenerated;

/**
 * The windowed diet types that live in `scheduledNutritionPlan` (carry startWeek/endWeek/sendDate):
 * static `ByCurrentWeight` (ranges) and ai `AiGenerated` (no ranges). AssignedByTrainer is a manual
 * trainer assignment, not part of the automation's scheduled windows.
 */
export type ScheduledNutritionWindow = ScheduledNutritionByCurrentWeight | ScheduledNutritionAiGenerated;

/**
 * Per-automation override of the tenant-level announcement channel settings (TRE-801).
 * The client still has exactly ONE channel — an override does not create another one; it
 * decides whether clients of this automation get it and, when they do, updates the
 * channel's name/type on activation (even when the channel already exists).
 */
export interface AutomationAnnouncementChannelOverride {
  /** inherit = tenant config decides; enabled/disabled force it for this automation's clients. */
  mode: "inherit" | "enabled" | "disabled";
  /** Overrides the tenant channel name; empty/null = inherit. */
  channelName?: string | null;
  /** Overrides the tenant channel type; null = inherit. */
  channelType?: ChatType.OneWayMarketing | ChatType.TwoWayMarketing | null;
}

export interface AutomationChatConfiguration {
  /** Legacy (pre-TRE-801): channel creation is gated by the tenant config now. */
  createMarketingChannel: boolean;
  sendByDefaultMessageFromAutomationToMarketingChannel: boolean | null;
  /** Legacy (pre-TRE-801): the channel name lives in the tenant config now. */
  marketingChannelTitle: string | null;
  announcementChannel?: AutomationAnnouncementChannelOverride | null;
}

/**
 * TRE-1603 (AI plan/diet automation v2) — guardrails for the deterministic
 * calorie/macro calculator (calculateNutritionTargets in @fitness-app/utils).
 * Every field optional: unset = calculator default.
 */
export interface AiNutritionTargetsGuardrails {
  /** Calorie adjustment per goal in percent (negative = deficit), keyed by ClientProfileGoal. */
  goalAdjustmentPct?: Partial<Record<string, number>>;
  minCalories?: number;
  maxCalories?: number;
  proteinPerKg?: number;
  fatPct?: number;
}

/**
 * TRE-1603 — config of the agent-generated diet (replaces the weight-range template
 * lists of scheduledNutritionPlan). Fired once the initial survey is answered:
 * ClientProfile -> calculateNutritionTargets(guardrails) -> generate-nutrition-plan-v3
 * trigger task -> auto-activation. NULL/disabled = legacy template flow.
 */
export interface AiNutritionPlanConfig {
  /**
   * TRE-1707 — stable id of the window. An automation holds a LIST of AI diet windows
   * (like scheduledNutritionPlan), so the id is what a scheduled task points back to.
   * Legacy single-object rows have none; `toAiNutritionPlans` fills in LEGACY_AI_CONFIG_ID.
   */
  id: string;
  enabled: boolean;
  /** Meals per day (v3 dto.mealsSchema). */
  mealsSchema: number;
  /** Generation model for the compose steps (same options as the trainer form). */
  model: string;
  /** Extra content-team instructions passed to the generator prompt. */
  prompt?: string | null;
  /** Dish tags biasing the candidate pool. */
  tags?: string[] | null;
  /**
   * TRE-1707 — meals_plan template ids the agent draws dishes from, the diet counterpart of
   * AiTrainingProgramConfig.referenceProgramIds. Their dishes seed the candidate pool ahead of
   * the similarity search; empty/unset = the generator's own retrieval only (previous behaviour).
   */
  referenceMealsPlanIds?: string[] | null;
  guardrails?: AiNutritionTargetsGuardrails | null;
  /** Plan validity window in automation weeks (mirrors "Diety od tygodnia X do Y"). */
  startWeek: number;
  endWeek: number | null;
  /** Activate the generated plan for the trainee automatically (no trainer review). */
  autoActivate: boolean;
  /**
   * TRE-1830 — run dish-photo generation for the freshly generated plan, the same
   * job the trainer fires by hand from the plan view. Off/absent keeps the plan
   * text-only; it costs per photo, so it is never implied.
   */
  generateDishImages?: boolean;
}

/**
 * TRE-1603 — config of the agent-generated training program. The generation task
 * builds a personalized program from the reference templates + ClientProfile
 * (trainingDays/place/experience/limitations) and schedules it for the client.
 */
export interface AiTrainingProgramConfig {
  /** TRE-1707 — stable id of the window; see AiNutritionPlanConfig.id. */
  id: string;
  enabled: boolean;
  /** training_program template ids the agent uses as references/building blocks. */
  referenceProgramIds: string[];
  /** Extra content-team guidelines for the generator prompt. */
  guidelines?: string | null;
  /** Generation model override; unset = task default. */
  model?: string | null;
  /** Program validity window in automation weeks. */
  startWeek: number;
  endWeek: number | null;
  /** Activate the generated program for the trainee automatically. */
  autoActivate: boolean;
}

export interface ProgramAutomation extends Omit<
  ProgramAutomationEntity,
  | "schedule"
  | "scheduledWorkoutsRoutine"
  | "scheduledNutritionPlan"
  | "welcomeMessages"
  | "chatConfiguration"
  | "packageAnchoredTasks"
  | "aiNutritionPlan"
  | "aiTrainingProgram"
  | "advisorEnabled"
  | "trainerChatEnabled"
> {
  schedule: Record<WeekNumber, AutomationWeek>;
  scheduledWorkoutsRoutine: WorkoutRoutine[];
  scheduledNutritionPlan?: ScheduledNutritionWindow[];
  welcomeMessages: (string | FileMessageContent)[];
  chatConfiguration: null | AutomationChatConfiguration;
  /** Tasks anchored to the client's package (start/end/progress) - kept outside the week/day grid. */
  packageAnchoredTasks?: ProgramAutomationScheduledTask[] | null;
  /**
   * AI-generated diet windows (v2); null = legacy scheduledNutritionPlan templates.
   * TRE-1707 — a list, like scheduledNutritionPlan. Rows written before TRE-1707 hold a bare
   * object, so ALWAYS read through `toAiNutritionPlans` instead of touching the field directly.
   */
  aiNutritionPlan?: AiNutritionPlanConfig | AiNutritionPlanConfig[] | null;
  /** AI-generated training program windows (v2); read through `toAiTrainingPrograms`. */
  aiTrainingProgram?: AiTrainingProgramConfig | AiTrainingProgramConfig[] | null;
  /**
   * TRE-1603 — explicit designation that this is a low-ticket 2.0 ("new type") automation
   * exposing the client-facing AI advisor. When true, adding the automation to a client stamps
   * `trainee.metadata.flags.clientAdvisor = Enabled`, which is what gates the advisor UI (mobile
   * + web) and the advisor chat endpoint. null/false = legacy automation: no advisor, no stamp,
   * 100% backward compatible. Offer touchpoints are NOT gated by this flag — they depend on the
   * client's product having an active, in-window offer (see ClientOffer audience window).
   */
  advisorEnabled?: boolean | null;
  /**
   * TRE-2184 — advisor-only automation. false = clients added through this automation get NO
   * trainer chat channel; the advisor is their only conversation and scheduled messages open
   * advisor conversations instead. Only meaningful with `advisorEnabled`; null/true = the
   * channel is created as always. Adding the automation stamps
   * `trainee.metadata.flags.trainerChat = Disabled` (cleared by any other automation, so an
   * upgrade to a trainer-led product brings the channel back).
   */
  trainerChatEnabled?: boolean;
}

/**
 * TRE-1707 — id given to a legacy single-object AI config so scheduled tasks can point at it.
 * Deterministic on purpose: a task scheduled before an admin re-save still resolves afterwards.
 */
export const LEGACY_AI_CONFIG_ID = "legacy";

const toAiConfigList = <T extends { id: string }>(value: T | T[] | null | undefined): T[] => {
  if (!value) {
    return [];
  }
  const list = Array.isArray(value) ? value : [value];
  return list.filter(Boolean).map((config) => (config.id ? config : { ...config, id: LEGACY_AI_CONFIG_ID }));
};

/**
 * The automation's AI diet windows, normalized from either shape (pre-TRE-1707 rows hold a bare
 * object). Every reader — admin, services, trigger tasks — must go through this.
 */
export const toAiNutritionPlans = (
  automation: Pick<ProgramAutomation, "aiNutritionPlan"> | null | undefined,
): AiNutritionPlanConfig[] => toAiConfigList(automation?.aiNutritionPlan);

/** The automation's AI training program windows; see `toAiNutritionPlans`. */
export const toAiTrainingPrograms = (
  automation: Pick<ProgramAutomation, "aiTrainingProgram"> | null | undefined,
): AiTrainingProgramConfig[] => toAiConfigList(automation?.aiTrainingProgram);

/** An AI window covers weeks [startWeek, endWeek]; endWeek === null means "to the end". */
export const isWeekInAiWindow = (config: { startWeek: number; endWeek: number | null }, weekNumber: number) =>
  config.startWeek <= weekNumber && (config.endWeek ?? Infinity) >= weekNumber;

/**
 * TRE-1707 — an AI training program window scheduled for a later week. The day-1 window is
 * provisioned at activation/survey; windows with startWeek > 1 become program_automation_task
 * rows carrying this payload, and the executor regenerates the program from `configId`.
 * The nutrition counterpart is ScheduledNutritionAiGenerated (ScheduledNutritionType.AiGenerated).
 */
export enum ScheduledTrainingProgramType {
  AiGenerated = "aiGeneratedTrainingProgram",
}

export interface ScheduledAiTrainingProgram {
  id: string;
  type: ScheduledTrainingProgramType.AiGenerated;
  /** AiTrainingProgramConfig.id this window regenerates from. */
  configId: string;
  startWeek: number;
  endWeek: number | null;
  sendDate: {
    hours: number;
    minutes: number;
    week: number;
    day: number;
  } | null;
}

export interface ProgramAutomationWithInitialSurvey extends ProgramAutomation {
  initialSurvey?: null | { name: string; title: string; id: string };
  additionalSurvey?: null | { name: string; title: string; id: string };
}

export enum ProgramAutomationTaskStatus {
  Upcoming = "upcoming",
  Done = "done",
  Rejected = "rejected",
  Canceled = "canceled",
  /**
   * Nothing to do this time, and that is expected — e.g. the challenge the task
   * enrolls into has no current edition. Distinct from `rejected` (real error
   * worth investigating) and `canceled` (filters excluded the client).
   */
  Skipped = "skipped",
}

export type UserId = string;

export interface ProgramAutomationTask extends Omit<ProgramAutomationTaskEntity, "task"> {
  task:
    | ProgramAutomationScheduledTask
    | ScheduledNutrition
    | ScheduledPopUpMessagesAutomation
    | ScheduledAiTrainingProgram;
  status: ProgramAutomationTaskStatus;
  addedBy: "productAutomation" | UserId;
}
